import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { publicApi } from '../../lib/api'
import { usePageMeta } from '../../hooks/usePageMeta'
import { Button } from '../../components/ui/button'
import { ArrowLeft, Loader2, MapPin, Calendar, Clock, Users } from 'lucide-react'

// Public, read-only event detail page. Adapted from the authenticated donor
// EventDetail page: fetches through publicApi regardless of session, and has
// no RSVP affordances — the public payload carries no RSVP data (no counts,
// no per-user status) and RSVPing requires an account, so a "Sign in to
// RSVP" link stands in for the RSVP button.
export default function PublicEventDetail() {
  const { id, eventId } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState(null)

  usePageMeta({
    title: event ? `${event.title} — Claritas` : 'Event — Claritas',
    description: event?.description || 'View this parish event.',
  })

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setNotFound(false)
      setError(null)
      try {
        const data = await publicApi.getParishEvent(id, eventId)
        if (cancelled) return
        setEvent(data)
      } catch (err) {
        if (cancelled) return
        console.error('Failed to fetch event:', err)
        if (err.status === 404) setNotFound(true)
        else setError(err.message || 'Failed to load event')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (id && eventId) load()
    return () => {
      cancelled = true
    }
  }, [id, eventId])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <h1 className="text-2xl font-bold font-display text-charcoal mb-2">
          We couldn't find that event
        </h1>
        <p className="text-charcoal-muted mb-6">
          It may have been removed, or the link may be incorrect.
        </p>
        <Link to={`/parishes/${id}/events`} className="text-gold font-semibold">
          Back to parish events &rarr;
        </Link>
      </div>
    )
  }

  if (error && !event) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <h1 className="text-2xl font-bold font-display text-charcoal mb-2">
          Something went wrong
        </h1>
        <p className="text-charcoal-muted mb-6">
          {error || "We couldn't load this event. Please try again."}
        </p>
        <Link to={`/parishes/${id}/events`} className="text-gold font-semibold">
          Back to parish events &rarr;
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate(`/parishes/${id}/events`)}
          className="text-charcoal hover:text-charcoal-muted mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>
      </div>

      {/* Cover Image */}
      {event.imageUrl && (
        <div className="relative w-full h-64 md:h-96 rounded overflow-hidden">
          <img
            src={event.imageUrl}
            alt={event.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-background-dark/50 to-transparent" />
        </div>
      )}

      <div className="bg-white border border-surface-border rounded p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold font-display text-charcoal mb-3">{event.title}</h1>

          {event.description && (
            <p className="text-base text-charcoal/90 leading-relaxed whitespace-pre-wrap">
              {event.description}
            </p>
          )}

          <div className="mt-4 pt-4 border-t border-surface-border space-y-3">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-charcoal-muted mt-0.5" />
              <div>
                <p className="text-charcoal">{formatDate(event.startTime)}</p>
                <p className="text-sm text-charcoal-muted">
                  {formatTime(event.startTime)}
                  {event.endTime ? ` - ${formatTime(event.endTime)}` : ''}
                </p>
              </div>
            </div>

            {event.location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-charcoal-muted mt-0.5" />
                <p className="text-charcoal">{event.location}</p>
              </div>
            )}

            {event.host?.name && (
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-charcoal-muted" />
                <p className="text-charcoal">Hosted by {event.host.name}</p>
              </div>
            )}
          </div>
        </div>

        <p className="text-sm text-charcoal-muted">
          <Link to="/sign-in" className="text-gold font-semibold">
            Sign in to RSVP
          </Link>{' '}
          for this event.
        </p>
      </div>

      {/* Upcoming Occurrences */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-gold" />
          <h3 className="text-xl font-semibold font-display text-charcoal">Upcoming Dates</h3>
        </div>

        {!event.upcomingOccurrences || event.upcomingOccurrences.length === 0 ? (
          <p className="text-charcoal-muted text-sm py-4 text-center">
            No upcoming dates scheduled.
          </p>
        ) : (
          <div className="space-y-3">
            {event.upcomingOccurrences.map((occ) => (
              <div
                key={occ.id}
                className="flex items-center justify-between py-3 px-4 rounded bg-surface-linen/50 border border-surface-border"
              >
                <div>
                  <p className="text-sm font-medium text-charcoal">
                    {formatDateTime(occ.startTime)}
                  </p>
                  {occ.endTime && (
                    <p className="text-xs text-charcoal-muted">to {formatTime(occ.endTime)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
